// reminders.ts — on-device LOCAL notifications (Tauri notification plugin). No server, no push
// service, no APNs, nothing leaves the device: the OS fires a scheduled reminder locally. Purely
// factual ("X renews tomorrow"), never advice — the app's observational fence stays intact.
//
// Best-effort by design: every call is wrapped so a denied permission, a web target (no plugin),
// or any plugin error NEVER blocks the app. Permission is only requested when there's actually
// something to schedule (contextual, not on cold launch).
import { isPermissionGranted, requestPermission, sendNotification, cancelAll, Schedule } from "@tauri-apps/plugin-notification";

export interface Reminder {
  /** Stable key (e.g. the row id) → a stable 32-bit notification id, so re-syncing replaces. */
  key: string;
  title: string;
  body: string;
  /** When to fire (local). Past dates are skipped. */
  at: Date;
}

// Deterministic 32-bit id from a string key (iOS notification ids must be i32).
function idFor(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (Math.imul(h, 31) + key.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

/** Clear this app's previously-scheduled reminders and (re)schedule one per FUTURE reminder.
 *  Idempotent — safe to call on every data change. Silent on any failure. */
export async function syncReminders(reminders: Reminder[]): Promise<void> {
  try {
    const now = Date.now();
    const future = reminders.filter(
      (r) => r.at instanceof Date && !Number.isNaN(r.at.getTime()) && r.at.getTime() > now,
    );
    if (future.length === 0) {
      try { await cancelAll(); } catch { /* nothing was scheduled */ }
      return;
    }
    let ok = await isPermissionGranted();
    if (!ok) ok = (await requestPermission()) === "granted";
    if (!ok) return;
    await cancelAll(); // drop the old set (handles removed/edited rows), then reschedule fresh
    for (const r of future) {
      sendNotification({ id: idFor(r.key), title: r.title, body: r.body, schedule: Schedule.at(r.at) });
    }
  } catch {
    /* notifications are a nicety — never let them throw into the UI */
  }
}
